<template>
  <ScaleLoader v-if="loading" />
  <div v-else>
    <ExportButtons
      :csv="exportGridsToExcel"
      :pdf="exportGridsToPdf"
      :xlsx="exportGridsToExcel"
    />
    <div class="row">
      <div class="col-sm-12">
        <Card
          :badges="3"
          :info-badge-description="[cardStore[0], cardStore[1], cardStore[2]]"
          :info-badge-title="[$t('components.reports.eventsList.totalDistance'), $t('components.reports.eventsList.drivingTime'), $t('components.reports.eventsList.breakTime')]"
          :status="$t('components.history.tabs.routeReport')"
          icon="bi bi-route"
        />
      </div>
    </div>
    <div class="row">
      <DxDataGrid
        :ref="hiddenDataGridRef"
        :data-source="store.details"
        style="display: none;"
      >
        <DxColumn :caption="$t('components.history.tabs.routeReport')">
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.totalDistance')"
            :filter-operations="allowedOperations"
            data-field="full_distance"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.drivingTime')"
            :filter-operations="allowedOperations"
            data-field="drive_time"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.breakTime')"
            :filter-operations="allowedOperations"
            data-field="rest_time"
          />
        </DxColumn>
      </DxDataGrid>
      <DxDataGrid
        :ref="dataGridRef"
        :allow-column-reordering="true"
        :allow-column-resizing="true"
        :column-auto-width="true"
        :column-hiding-enabled="true"
        :data-source="store.events_list"
        :hover-state-enabled="true"
        :remote-operations="remoteOperations"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="false"
        class="route-report-grid"
      >
        <DxHeaderFilter :visible="true" />
        <DxPaging
          :page-index="0"
          :page-size="25"
        />
        <DxPager
          :allowed-page-sizes="[10, 20, 25]"
          :show-info="true"
          :show-navigation-buttons="true"
          :show-page-size-selector="true"
        />
        <DxFilterRow :visible="true" />
        <DxColumnFixing :enabled="true" />
        <DxColumn :caption="$t('components.history.tabs.routeReport')">
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.start')"
            :filter-operations="allowedOperations"
            data-field="start_timestamp"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.stop')"
            :filter-operations="allowedOperations"
            data-field="stop_timestamp"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.ignition.ignition')"
            :filter-operations="allowedOperations"
            data-field="ignition"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.time')"
            :filter-operations="allowedOperations"
            data-field="time"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.distance')"
            :filter-operations="allowedOperations"
            data-field="distance"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.routeReport.startLocation')"
            :filter-operations="allowedOperations"
            data-field="place_start_link"
            :allow-sorting="false"
            cell-template="startLocationTemplate"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.routeReport.stopLocation')"
            :filter-operations="allowedOperations"
            data-field="place_stop_link"
            :allow-sorting="false"
            cell-template="stopLocationTemplate"
          />
        </DxColumn>
        <template #startLocationTemplate="{ data }">
          <a
            :href="generateGoogleMapsLink(data.data.place_start)"
            target="_blank"
            class="location-link"
          >
            {{ getLocationText(data.data, 'start') }}
          </a>
        </template>
        <template #stopLocationTemplate="{ data }">
          <a
            :href="generateGoogleMapsLink(data.data.place_stop)"
            target="_blank"
            class="location-link"
          >
            {{ getLocationText(data.data, 'stop') }}
          </a>
        </template>
      </DxDataGrid>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import Card from '@/components/extras/Card.vue'
import { getEventsListReport, getReverseGeocode } from '@/crud/getters'
import {
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging
} from 'devextreme-vue/data-grid'
import { Workbook } from 'exceljs'
import { saveAs } from 'file-saver'
import { exportDataGrid } from 'devextreme/excel_exporter'
import { getHumanDateTime, minutesToHoursMinutes, secondsToHoursMinutes } from '@/utils/tools'
import ExportButtons from '@/components/content/_partials/ExportButtons.vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter'
import { importFont } from '@/utils/jspdf/importFont'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import 'jspdf-autotable'

export default defineComponent({
  name: 'RouteReport',
  components: {
    ExportButtons,
    DxColumn,
    DxColumnFixing,
    DxDataGrid,
    DxFilterRow,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    Card,
    ScaleLoader
  },
  props: {
    imei: BigInt as unknown as PropType<bigint>,
    dateFrom: String,
    dateTo: String,
    vehicle: Object
  },
  data(): {
    store: any,
    cardStore: Array<any>,
    allowedOperations: Array<string>,
    remoteOperations: boolean,
    dataGridRef: string,
    hiddenDataGridRef: string,
    loading: boolean,
    visibilityObserver: any,
    addrCache: Record<string, string>,
    geocodingActive: boolean
  } {
    return {
      store: { details: [], events_list: [] },
      cardStore: [],
      allowedOperations: ['contains', '=', 'notcontains', '!='],
      remoteOperations: true,
      dataGridRef: 'routeReportGrid',
      hiddenDataGridRef: 'hiddenRouteReportGrid',
      loading: true,
      visibilityObserver: null,
      addrCache: {} as Record<string, string>,
      geocodingActive: false
    }
  },
  computed: {
    routeReportGridInstance() {
      const self: any = this
      try {
        return self.$refs[this.dataGridRef]?.instance
      } catch (error) {
        console.warn('RouteReportGrid instance not available:', error)
        return null
      }
    },
    hiddenRouteReportGridInstance() {
      const self: any = this
      try {
        return self.$refs[this.hiddenDataGridRef]?.instance
      } catch (error) {
        console.warn('HiddenRouteReportGrid instance not available:', error)
        return null
      }
    }
  },
  methods: {
    generateGoogleMapsLink(coordinates: { lat: number, lng: number }): string {
      if (!coordinates || !coordinates.lat || !coordinates.lng) {
        return '#'
      }
      return `https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`
    },
    formatCoordinates(coordinates: { lat: number, lng: number }): string {
      if (!coordinates || !coordinates.lat || !coordinates.lng) {
        return 'N/A'
      }
      return `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`
    },
    formatDistance(distance: { map_distance: number, can_distance: number }): string {
      if (!distance) {
        return 'N/A'
      }
      return `Map: ${distance.map_distance?.toFixed(2) || 0} km, CAN: ${distance.can_distance?.toFixed(2) || 0} km`
    },
    exportGridsToExcel(extension: string) {
      const self: any = this
      const workbook = new Workbook()

      const sheet = workbook
          .addWorksheet(self.$t('components.history.tabs.routeReport'))

      const secondSheet = workbook
          .addWorksheet(self.$t('components.history.tabs.routeReport') + ' #2')

      exportDataGrid({
        worksheet: sheet,
        component: self.hiddenRouteReportGridInstance
      })
          .then(() => exportDataGrid({
            worksheet: secondSheet,
            component: self.routeReportGridInstance
          }))
          .then(() => {
            if (extension == 'csv')
              workbook.csv.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                    `Route_report_${ self.imei }.csv`)
              })
            else if (extension == 'xlsx')
              workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                    `Route_report_${ self.imei }.xlsx`)
              })
          })
    },
    exportGridsToPdf() {
      const doc = new jsPDF()
      importFont(doc)

      exportDataGridToPdf({
        jsPDFDocument: doc,
        component: this.routeReportGridInstance,
      })
      .then(() => {
        doc.save(`Route_report_${this.imei}.pdf`)
      })
    },
    loadData() {
      const self: any = this
      if (this.imei && this.dateFrom && this.dateTo) {
        this.loading = true
        // Reset data
        this.store = { details: [], events_list: [] }
        this.cardStore = []
        
        getEventsListReport((this.imei as bigint), this.dateFrom, this.dateTo)
            .then((res) => {
              this.store = res.data

              // Format details data for display
              if (this.store.details && this.store.details.length > 0) {
                const details = this.store.details[0]
                details.drive_time = minutesToHoursMinutes(details.drive_time)
                details.rest_time = minutesToHoursMinutes(details.rest_time)
                
                // Format full_distance for display
                if (details.full_distance) {
                  const formattedDistance = this.formatDistance(details.full_distance)
                  this.cardStore.push(formattedDistance, details.drive_time, details.rest_time)
                } else {
                  this.cardStore.push('N/A', details.drive_time, details.rest_time)
                }
              }

              // Format events_list data
              if (this.store.events_list && this.store.events_list.length > 0) {
                this.store.events_list.forEach((obj) => {
                  obj.start_timestamp = getHumanDateTime(obj.start_timestamp)
                  obj.stop_timestamp = getHumanDateTime(obj.stop_timestamp)
                  obj.time = secondsToHoursMinutes(obj.time)
                  
                  // Format distance for display in the table
                  if (obj.distance) {
                    obj.distance = this.formatDistance(obj.distance)
                  }
                  
                  // Add formatted location links for export
                  obj.place_start_link = this.formatCoordinates(obj.place_start)
                  obj.place_stop_link = this.formatCoordinates(obj.place_stop)
                })
              }

              this.loading = false

              // Odśwież gridy po załadowaniu danych
              this.$nextTick(() => {
                this.refreshGrids()
              })
            })
            .catch(() => {
              self.$swal.fire({
                icon: 'warning',
                title: 'Oops...',
                text: this.$t('components.error')
              })
              this.loading = false
              
              // Rozpocznij geokodowanie po załadowaniu danych
              this.startGeocoding()
            })
      }
    },
    getLocationText(rowData: any, type: 'start' | 'stop'): string {
      const coordinates = type === 'start' ? rowData.place_start : rowData.place_stop
      const cacheKey = `${coordinates.lat},${coordinates.lng}`
      
      // Zwróć adres z cache lub współrzędne
      return this.addrCache[cacheKey] || this.formatCoordinates(coordinates)
    },
    startGeocoding() {
      if (!this.geocodingActive && this.isVisible()) {
        this.geocodingActive = true
        this.geocodeLocations()
      }
    },
    stopGeocoding() {
      this.geocodingActive = false
    },
    async geocodeLocations() {
      if (!this.geocodingActive || !this.store.events_list.length) return
      
      const locations = new Set<string>()
      
      // Zbierz wszystkie unikalne lokalizacje
      this.store.events_list.forEach((event: any) => {
        if (event.place_start) {
          const startKey = `${event.place_start.lat},${event.place_start.lng}`
          if (!this.addrCache[startKey]) {
            locations.add(startKey)
          }
        }
        if (event.place_stop) {
          const stopKey = `${event.place_stop.lat},${event.place_stop.lng}`
          if (!this.addrCache[stopKey]) {
            locations.add(stopKey)
          }
        }
      })
      
      // Geokoduj po kolei z opóźnieniem
      for (const location of locations) {
        if (!this.geocodingActive) break
        
        try {
          const response = await getReverseGeocode(location)
          this.addrCache[location] = response.data || ''
          
          // Odśwież tabelę
          this.$forceUpdate()
          
          // Opóźnienie między requestami
          await new Promise(resolve => setTimeout(resolve, 500))
        } catch (error) {
          console.error('Geocoding error for', location, error)
          this.addrCache[location] = ''
        }
      }
    },
    isVisible() {
      // Sprawdź czy komponent jest widoczny (zakładka jest aktywna)
      return this.$el && this.$el.offsetParent !== null
    },
    refreshGrids() {
      // Odśwież gridy DevExtreme po zmianie widoczności
      try {
        if (this.routeReportGridInstance) {
          this.routeReportGridInstance.repaint()
        }
        if (this.hiddenRouteReportGridInstance) {
          this.hiddenRouteReportGridInstance.repaint()
        }
      } catch (error) {
        console.warn('Grid refresh error:', error)
      }
    },
    setupVisibilityWatcher() {
      // Obserwuj zmiany widoczności komponentu
      if (this.$el) {
        const observer = new MutationObserver(() => {
          if (this.isVisible()) {
            if (!this.store.events_list.length) {
              this.loadData()
            } else {
              this.startGeocoding()
            }
            // Odśwież gridy po pokazaniu komponentu
            setTimeout(() => {
              this.refreshGrids()
            }, 100)
          } else {
            this.stopGeocoding()
          }
        })
        
        observer.observe(this.$el.parentElement, {
          attributes: true,
          attributeFilter: ['style'],
          subtree: true
        })
        
        // Zapisz observer do późniejszego czyszczenia
        this.visibilityObserver = observer
      }
    }
  },
  mounted() {
    // Sprawdź czy komponent jest widoczny przy mounted i załaduj dane
    this.$nextTick(() => {
      if (this.isVisible()) {
        this.loadData()
      }
    })
    
    // Obserwuj zmiany widoczności
    this.setupVisibilityWatcher()
  },
  watch: {
    imei() {
      if (this.isVisible()) {
        this.loadData()
      }
    },
    dateFrom() {
      if (this.isVisible()) {
        this.loadData()
      }
    },
    dateTo() {
      if (this.isVisible()) {
        this.loadData()
      }
    }
  },
  beforeUnmount() {
    // Zatrzymaj geokodowanie i wyczyść observer przy usuwaniu komponentu
    this.stopGeocoding()
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
    }
  }
})
</script>

<style lang="scss" scoped>
.btn-custom {
  display: block;
  margin: 0 0 0 auto;
}

button {
  i {
    font-size: 2rem;
  }
}

.location-link {
  color: #f67f01 !important;
  text-decoration: none;
  display: inline-block;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
  line-height: 1.2;

  &:hover {
    color: #333 !important;
    text-decoration: underline;
  }
}

@media (max-width: 768px) {
  .route-report-grid {
    // Wyłącz tylko główne przewijanie, ale zachowaj funkcjonalność DevExtreme
    :deep(.dx-scrollable-wrapper) {
      overflow-x: auto !important;
      overflow-y: visible !important;
    }

    :deep(.dx-scrollable-container) {
      overflow: visible !important;
    }

    :deep(.dx-scrollable-content) {
      overflow: visible !important;
    }

    // Zachowaj podstawową funkcjonalność tabeli, ale wyłącz vertykalne przewijanie
    :deep(.dx-datagrid-rowsview) {
      overflow-y: visible !important;
      max-height: none !important;
    }

    // Upewnij się że tabela zajmuje pełną szerokość
    :deep(.dx-datagrid-table) {
      width: 100% !important;
    }
  }
}
</style>